(ns common.transaction-process.hooks
  "The transaction-process hook registry — the bounded escape hatch for logic the
   data DSL can't express (e.g. CapMan's reservation prefill).

   Two firm rules:
   1. Templates reference hooks by *keyword*, never embedded fns — templates stay
      pure data: serializable, diffable, malli-validatable.
   2. Customer-namespaced code registers `hook-key -> fn` here; the shared engine
      calls hooks generically via `run-hook` and contains zero `if capman?` /
      tenant branching. Adding a customer hook = a new entry in a customer hook
      namespace + a merge below; using one = a keyword in template data.

   Lifecycle taxonomy (the real extension API): `:hook/prefill` (seed a new
   sub-entity instance from ancestors) and `:hook/transform-write` (rewrite a
   pending update map). Each hook takes a single context map. This mirrors the
   template registry (data + a lookup), not a mutable atom, so it is pure and
   reloadable."
  (:require
   [common.transaction-process.templates.capman.hooks :as capman-hooks]))

(def lifecycle-phases
  "The closed set of hook lifecycle phases. A dictionary entity-slot attaches
   hooks to a phase via `:slot/hooks {phase hook-key}` (the template root carries
   entity-level ones via `:template/hooks`); the engine fires the phase generically
   at the matching point, passing a single rich context map. Adding a phase = code
   (a new firing point in the engine); using one = a keyword in template data.

   - :hook/prefill        — seed a new sub-entity instance's initial field/child values
                            when its create form opens. (wired: decision create form)
   - :hook/transform-write — pure `(prev-entity, updates-map) -> updates-map'`
                            transform fired before a TP entity's update is
                            committed. The hook for derived fields (e.g. CapMan
                            seeds `ic-decision-needed` when the anticipated IC date
                            is first set). Runs backend-authoritatively so the
                            invariant holds for update paths.
                            (wired: backend `transform-update-data`)"
  #{:hook/prefill :hook/transform-write})

(def registry
  "hook-key -> fn. Customer hook namespaces contribute their maps here."
  (merge capman-hooks/hooks))

(defn get-hook
  [hook-key]
  (get registry hook-key))

(defn run-hook
  "Invoke the hook registered under `hook-key` with `ctx`. An unknown or nil
   hook-key is a no-op returning nil (an absent optional hook is not an error)."
  [hook-key ctx]
  (when-let [f (get registry hook-key)]
    (f ctx)))

(defn run-transform-write
  "Fold the `:hook/transform-write` hooks (a vector of hook-keys, in order) over a
   pending `updates` map. Each hook is a pure `{:prev :updates :ctx} -> updates'`
   transform; a hook returning nil leaves the map unchanged (absent/optional hook
   semantics). Returns the final updates map. Generic — the caller supplies the
   ordered hook-keys (from a template's `:template/hooks`) so this stays free of
   any customer/template branching."
  [{:keys [hook-keys prev updates ctx]}]
  (reduce (fn [upd hook-key]
            (or (run-hook hook-key {:prev prev :updates upd :ctx ctx})
                upd))
          updates
          hook-keys))
